import { TRAITS } from './model.ts'
import type {
  AnomalyScenario,
  MetaphorChip,
  MetaphorEvaluation,
  MetaphorSelection,
  StabilizationRecord,
  Trait,
} from './model.ts'

export const MAX_METAPHOR_LENGTH = 180

export function normalizeMetaphorInput(input: string): string {
  const compact = input.normalize('NFKC').replace(/\s+/gu, ' ').trim()
  return Array.from(compact).slice(0, MAX_METAPHOR_LENGTH).join('')
}

export function isTrait(value: unknown): value is Trait {
  return (
    typeof value === 'string' &&
    (TRAITS as readonly string[]).includes(value)
  )
}

export function canonicalizeTraits(
  values: readonly unknown[],
): readonly Trait[] {
  return TRAITS.filter((trait) =>
    values.some((value) => isTrait(value) && value === trait),
  )
}

function normalizedSelection(
  scenario: AnomalyScenario,
  selection: MetaphorSelection,
): MetaphorSelection {
  const normalized: Record<string, string> = {}

  for (const slot of scenario.slots) {
    const chipId = selection[slot.id]
    if (chipId !== undefined) {
      normalized[slot.id] = chipId
    }
  }

  return normalized
}

export function findChip(
  scenario: AnomalyScenario,
  slotId: string,
  chipId: string,
): MetaphorChip | undefined {
  const slot = scenario.slots.find((candidate) => candidate.id === slotId)
  return slot?.chips.find((candidate) => candidate.id === chipId)
}

export function isSelectionComplete(
  scenario: AnomalyScenario,
  selection: MetaphorSelection,
): boolean {
  return scenario.slots.every((slot) => {
    const chipId = selection[slot.id]
    return chipId !== undefined && findChip(scenario, slot.id, chipId) !== undefined
  })
}

export function composeMetaphor(
  scenario: AnomalyScenario,
  selection: MetaphorSelection,
): string {
  const fragments = scenario.slots.map((slot) => {
    const chipId = selection[slot.id]
    const chip = chipId === undefined ? undefined : findChip(scenario, slot.id, chipId)
    return chip?.text ?? '〈' + slot.label + ' 선택〉'
  })

  return scenario.sentenceLead + fragments.join(' ')
}

export function deriveInheritedTraits(
  scenario: AnomalyScenario,
  successes: readonly StabilizationRecord[],
): readonly Trait[] {
  if (scenario.inheritance === 'none') {
    return []
  }

  return canonicalizeTraits(
    successes.flatMap((record) => record.effectiveTraits),
  )
}

export function evaluateInterpretation(
  scenario: AnomalyScenario,
  selection: MetaphorSelection,
  interpretedTraits: readonly Trait[],
  metaphor: string,
  inheritedTraits: readonly Trait[] = [],
): MetaphorEvaluation {
  const slots = scenario.slots.map((slot) => {
    const chipId = selection[slot.id] ?? null
    return {
      slotId: slot.id,
      chipId,
      valid: chipId !== null && findChip(scenario, slot.id, chipId) !== undefined,
    }
  })
  // Traits are supplied by the meaning interpreter. The rule engine never
  // derives meaning from chip ids or text.
  const canonicalInterpretedTraits = canonicalizeTraits(interpretedTraits)
  const canonicalInheritedTraits = canonicalizeTraits(inheritedTraits)
  const effectiveTraits = canonicalizeTraits([
    ...canonicalInterpretedTraits,
    ...canonicalInheritedTraits,
  ])
  const missingRequiredTraits = scenario.requiredTraits.filter(
    (trait) => !effectiveTraits.includes(trait),
  )
  const triggeredForbiddenTraits = scenario.forbiddenTraits.filter((trait) =>
    effectiveTraits.includes(trait),
  )
  const complete = slots.every((slot) => slot.valid)

  return {
    scenarioId: scenario.id,
    // The player's actual edit is normalized only for reproducible recording.
    // It is never inspected to decide the verdict.
    sentence: normalizeMetaphorInput(metaphor),
    selection: normalizedSelection(scenario, selection),
    slots,
    complete,
    stable:
      complete &&
      missingRequiredTraits.length === 0 &&
      triggeredForbiddenTraits.length === 0,
    interpretedTraits: canonicalInterpretedTraits,
    inheritedTraits: canonicalInheritedTraits,
    effectiveTraits,
    requiredTraits: [...scenario.requiredTraits],
    forbiddenTraits: [...scenario.forbiddenTraits],
    missingRequiredTraits,
    triggeredForbiddenTraits,
  }
}

export function evaluateWithHistory(
  scenario: AnomalyScenario,
  selection: MetaphorSelection,
  interpretedTraits: readonly Trait[],
  metaphor: string,
  successes: readonly StabilizationRecord[],
): MetaphorEvaluation {
  return evaluateInterpretation(
    scenario,
    selection,
    interpretedTraits,
    metaphor,
    deriveInheritedTraits(scenario, successes),
  )
}

export const evaluateMetaphor = evaluateInterpretation
